import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import GeolocationCheck from '@/geolocation/icons/GeolocationCheck';
import GeolocationPin from '@/geolocation/icons/GeolocationPin';
import { GeolocationNote } from '@/geolocation/types';
import { getRegion } from '@/utilities/getters/cookieGetters';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';

const VERIFIED_VISIBLE_MS = 1000;
const VERIFIED_DISMISS_MS = 500;

export const MessageNote = ({
  type,
  dismissing,
  onDismissed,
}: GeolocationNote) => {
  const { t } = useTranslation();
  const messageString = `geocomply.locationNote.${type}`;
  const message = `${t(messageString)}`;

  return (
    <div
      className={`location-note ${type}${dismissing ? ' dismissing' : ''}`}
      role={type === 'verified' ? 'status' : undefined}
      aria-live={type === 'verified' ? 'polite' : undefined}
      data-testid={
        type === 'pending' ? 'geolocation-verifying-location' : undefined
      }
      onAnimationEnd={dismissing ? onDismissed : undefined}
    >
      {type === 'verified' ? <GeolocationCheck /> : <GeolocationPin />}
      <span>{message}</span>
    </div>
  );
};

export const VerifiedToast = ({ onClose }: { onClose: () => void }) => {
  const [dismissing, setDismissing] = useState(false);
  const dismissedRef = useRef(false);

  const finishDismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const t = setTimeout(() => setDismissing(true), VERIFIED_VISIBLE_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!dismissing) return;
    const fallback = setTimeout(finishDismiss, VERIFIED_DISMISS_MS);
    return () => clearTimeout(fallback);
  }, [dismissing, finishDismiss]);

  return (
    <MessageNote
      type="verified"
      dismissing={dismissing}
      onDismissed={finishDismiss}
    />
  );
};

type LocationNoteProps = {
  geolocationInProgress: boolean;
  geolocationFailed: boolean;
  geolocationSuccess: boolean;
  geolocationToken: string | null;
  previouslyVerified: boolean;
};

const PendingLocationNote = ({
  onPendingShown,
}: {
  onPendingShown: () => void;
}) => {
  useLayoutEffect(() => {
    onPendingShown();
  }, [onPendingShown]);

  return <MessageNote type="pending" />;
};

const LocationNoteBody = memo(function LocationNoteBody({
  geolocationInProgress,
  geolocationFailed,
  geolocationSuccess,
  geolocationToken,
  previouslyVerified,
}: LocationNoteProps) {
  const [sawPending, setSawPending] = useState(false);
  const [verifiedDismissed, setVerifiedDismissed] = useState(false);

  const isPending = geolocationInProgress && !geolocationToken;
  const showVerified =
    sawPending &&
    (geolocationSuccess || Boolean(geolocationToken)) &&
    !geolocationInProgress &&
    !verifiedDismissed;

  const handlePendingShown = useCallback(() => {
    setSawPending(true);
    setVerifiedDismissed(false);
  }, []);

  const handleVerifiedClose = useCallback(() => {
    setVerifiedDismissed(true);
  }, []);

  if (geolocationFailed || previouslyVerified) return null;

  if (isPending)
    return <PendingLocationNote onPendingShown={handlePendingShown} />;

  if (showVerified) return <VerifiedToast onClose={handleVerifiedClose} />;

  return null;
});

export const LocationNote = () => {
  const geo = useStore(
    useShallow((s) => ({
      geolocationInProgress: s.geolocationStatus.geolocationInProgress,
      geolocationFailed: s.geolocationStatus.geolocationFailed,
      geolocationSuccess: s.geolocationStatus.geolocationSuccess,
      geolocationToken: s.geolocationContext.geolocationToken,
      previouslyVerified: s.geolocationStatus.previouslyVerified,
    })),
  );

  return <LocationNoteBody {...geo} />;
};

export const FooterNote = memo(function FooterNote() {
  const region = getRegion();
  const { t } = useTranslation();

  return (
    <div className="footer-note" data-testid="geolocation-footer-text">
      <p>
        {t('geocomply.footerNote1')}
        {t(`regions.${region}`)}.
      </p>
      <p>{t('geocomply.footerNote2')}</p>
    </div>
  );
});
